// Column definitions (shared by per-table CSV export) + the full report generator.
import { fetchAllRows } from "@/lib/csv";

const TIER_LABELS = { standard: "VIP", pro: "PRO", premium: "CAPITAL", business: "TEAMS" };
const fmtUnix = (ts) => (ts ? new Date(ts * 1000).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");
const fmtISO = (s) => (s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");
const fmtISOTime = (s) => (s ? new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "");
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export const SUB_COLS = [
  { label: "Email", value: (r) => r.email },
  { label: "First name", value: (r) => r.first_name },
  { label: "Last name", value: (r) => r.last_name },
  { label: "Tier", value: (r) => TIER_LABELS[r.tier] || r.tier },
  { label: "Billing", value: (r) => (r.subscription_interval === "year" ? "Annual" : r.subscription_interval === "month" ? "Monthly" : "") },
  { label: "Status", value: (r) => r.subscription_status },
  { label: "Period end", value: (r) => fmtUnix(r.subscription_current_period_end) },
  { label: "Cancels at period end", value: (r) => (r.subscription_cancel_at_period_end ? "yes" : "no") },
  { label: "Scheduled tier", value: (r) => r.subscription_scheduled_tier || "" },
  { label: "Joined", value: (r) => fmtISO(r.created_at) },
];

export const INV_COLS = [
  { label: "Date", value: (r) => fmtUnix(r.created) },
  { label: "Member", value: (r) => r.email },
  { label: "Invoice #", value: (r) => r.number },
  { label: "Amount", value: (r) => Number(r.amount).toFixed(2) },
  { label: "Paid", value: (r) => Number(r.amount_paid).toFixed(2) },
  { label: "Currency", value: (r) => r.currency },
  { label: "Status", value: (r) => r.status },
  { label: "Period start", value: (r) => fmtUnix(r.period_start) },
  { label: "Period end", value: (r) => fmtUnix(r.period_end) },
  { label: "Reason", value: (r) => r.billing_reason },
  { label: "Invoice URL", value: (r) => r.hosted_invoice_url },
];

export const EMAIL_COLS = [
  { label: "Date", value: (r) => fmtISOTime(r.created_at) },
  { label: "To", value: (r) => r.to },
  { label: "Subject", value: (r) => r.subject },
  { label: "Tag", value: (r) => r.tag },
  { label: "Mode", value: (r) => r.mode },
  { label: "Status", value: (r) => r.status },
];

export const TC_COLS = [
  { label: "Date", value: (r) => fmtISOTime(r.created_at) },
  { label: "Member", value: (r) => r.email || r.user_id },
  { label: "From", value: (r) => TIER_LABELS[r.from_tier] || r.from_tier },
  { label: "To", value: (r) => TIER_LABELS[r.to_tier] || r.to_tier },
  { label: "Direction", value: (r) => r.direction },
  { label: "Source", value: (r) => r.source },
];

const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));

function htmlTable(title, columns, rows) {
  const head = columns.map((c) => `<th>${esc(c.label)}</th>`).join("");
  const body = rows.length
    ? rows.map((r) => `<tr>${columns.map((c) => `<td>${esc(typeof c.value === "function" ? c.value(r) : r[c.key])}</td>`).join("")}</tr>`).join("")
    : `<tr><td colspan="${columns.length}" class="empty">No records in this range.</td></tr>`;
  return `<section><h2>${esc(title)} <span class="count">(${rows.length})</span></h2>
    <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></section>`;
}

function kpiCard(label, value, sub) {
  return `<div class="kpi"><div class="kpi-label">${esc(label)}</div><div class="kpi-value">${esc(value)}</div>${sub ? `<div class="kpi-sub">${esc(sub)}</div>` : ""}</div>`;
}

function buildHtml(summary, ds, from, to) {
  const b = summary.billing, m = summary.members;
  const generated = new Date().toLocaleString();
  const kpis = [
    kpiCard("Active subscribers", b.active_subscribers),
    kpiCard("MRR (gross est.)", money(b.mrr), `ARR ${money(b.arr)}`),
    kpiCard("Payments collected", money(b.payments.paid_amount), `${b.payments.paid_count} invoices`),
    kpiCard("Failed payments", b.payments.failed_count),
    kpiCard("Cancellations scheduled", b.cancel_scheduled),
    kpiCard("New members", m.new_in_range, "in range"),
    kpiCard("New subscriptions", b.new_subscribers, "in range"),
    kpiCard("Total members", m.total),
  ].join("");

  const revenueRows = (summary.revenue_series || []).map((r) => `<tr><td>${esc(r.period)}</td><td>${money(r.amount)}</td></tr>`).join("") || `<tr><td colspan="2" class="empty">No paid invoices in range.</td></tr>`;
  const tierRows = Object.entries(m.by_tier || {}).map(([k, v]) => `<tr><td>${esc(TIER_LABELS[k] || k)}</td><td>${v}</td></tr>`).join("");
  const emailTagRows = Object.entries(summary.email.by_tag || {}).map(([k, v]) => `<tr><td>${esc(k)}</td><td>${v}</td></tr>`).join("") || `<tr><td colspan="2" class="empty">None</td></tr>`;
  const emailMode = Object.entries(summary.email.by_mode || {}).map(([k, v]) => `${k === "mailersend" ? "Delivered" : "Log"}: ${v}`).join(" · ");

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Startup Otter — Analytics Report</title>
<style>
  :root { --teal:#0d9488; --ink:#0f172a; --muted:#64748b; --line:#e2e8f0; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif; color: var(--ink); margin: 0; padding: 28px; background:#fff; }
  .bar { display:flex; justify-content:space-between; align-items:center; border-bottom:2px solid var(--teal); padding-bottom:14px; margin-bottom:18px; }
  .brand { font-size:20px; font-weight:800; } .brand span { color: var(--teal); }
  .meta { text-align:right; font-size:12px; color: var(--muted); }
  .printbtn { background:var(--teal); color:#fff; border:none; padding:9px 16px; border-radius:8px; font-size:13px; font-weight:600; cursor:pointer; }
  h1 { font-size:18px; margin:0 0 2px; } .sub { color:var(--muted); font-size:13px; margin:0 0 18px; }
  .kpis { display:grid; grid-template-columns:repeat(4,1fr); gap:10px; margin-bottom:22px; }
  .kpi { border:1px solid var(--line); border-radius:10px; padding:12px 14px; }
  .kpi-label { font-size:11px; color:var(--muted); text-transform:uppercase; letter-spacing:.03em; }
  .kpi-value { font-size:22px; font-weight:760; margin-top:2px; }
  .kpi-sub { font-size:11px; color:var(--muted); }
  .row2 { display:grid; grid-template-columns:1fr 1fr 1fr; gap:14px; margin-bottom:8px; }
  section { margin:18px 0; page-break-inside:auto; }
  h2 { font-size:14px; margin:0 0 8px; border-bottom:1px solid var(--line); padding-bottom:6px; }
  .count { color:var(--muted); font-weight:400; font-size:12px; }
  table { width:100%; border-collapse:collapse; font-size:11.5px; }
  th { text-align:left; background:#f1f5f9; padding:6px 8px; font-size:10px; text-transform:uppercase; letter-spacing:.03em; color:var(--muted); border-bottom:1px solid var(--line); }
  td { padding:6px 8px; border-bottom:1px solid #f1f5f9; vertical-align:top; }
  .empty { color:var(--muted); text-align:center; padding:14px; }
  @media print { .printbtn { display:none; } body { padding:0; } }
</style></head><body>
  <div class="bar">
    <div class="brand">Startup<span>Otter</span></div>
    <div class="meta">Analytics Report<br/>Generated ${esc(generated)}<button class="printbtn" style="margin-left:12px" onclick="window.print()">Print / Save as PDF</button></div>
  </div>
  <h1>Platform analytics</h1>
  <p class="sub">Date range: ${esc(from)} → ${esc(to)} (granularity: ${esc(summary.range.granularity)})</p>

  <div class="kpis">${kpis}</div>

  <div class="row2">
    <section><h2>Revenue by month</h2><table><thead><tr><th>Month</th><th>Collected</th></tr></thead><tbody>${revenueRows}</tbody></table></section>
    <section><h2>Members by tier</h2><table><thead><tr><th>Tier</th><th>Members</th></tr></thead><tbody>${tierRows}</tbody></table></section>
    <section><h2>Email health <span class="count">${esc(emailMode)}</span></h2><table><thead><tr><th>Tag</th><th>Sent</th></tr></thead><tbody>${emailTagRows}</tbody></table></section>
  </div>

  ${htmlTable("Active subscribers", SUB_COLS, ds.subs)}
  ${htmlTable("Invoices", INV_COLS, ds.invoices)}
  ${htmlTable("Emails", EMAIL_COLS, ds.emails)}
  ${htmlTable("Tier changes", TC_COLS, ds.tcs)}

  <script>window.addEventListener('load', function(){ setTimeout(function(){ window.print(); }, 400); });</script>
</body></html>`;
}

// Pull all datasets for the current range and open a print-ready report window.
export async function generateReport(api, summary, from, to) {
  const [subs, invAll, emails, tcs] = await Promise.all([
    fetchAllRows(api, "/admin/analytics/subscribers", { status: "active" }),
    fetchAllRows(api, "/admin/analytics/invoices", {}, { cursor: true }),
    fetchAllRows(api, "/admin/analytics/emails", { from, to }),
    fetchAllRows(api, "/admin/analytics/tier-changes", { from, to }),
  ]);
  const fromTs = Date.parse(`${from}T00:00:00Z`) / 1000;
  const toTs = Date.parse(`${to}T23:59:59Z`) / 1000;
  const invoices = invAll.filter((i) => i.created >= fromTs && i.created <= toTs);

  const html = buildHtml(summary, { subs, invoices, emails, tcs }, from, to);
  const w = window.open("", "_blank");
  if (!w) throw new Error("popup-blocked");
  w.document.write(html);
  w.document.close();
}
